import React, { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function AuditLogList({ scope = "admin", projectId, pageSize: initialPageSize = 25 }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("desc");
  const [pageSize, setPageSize] = useState(initialPageSize);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const path = scope === "client" ? "/client/audit-logs" : "/admin/audit-logs";
        const params = { skip, limit: pageSize, sort };
        if (projectId) params.project_id = projectId;
        const r = await api.get(path, { params });
        if (cancelled) return;
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      } catch (e) {
        if (!cancelled) toast.error(apiErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scope, projectId, skip, pageSize, sort]);

  const page = Math.floor(skip / pageSize) + 1;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-200 flex items-center gap-3">
        <div className="text-xs text-zinc-500">{total} entries</div>
        <button onClick={() => setSort(sort === "desc" ? "asc" : "desc")} className="text-xs text-zinc-600 hover:text-zinc-900" data-testid="audit-sort-toggle">
          Sorted {sort === "desc" ? "newest first" : "oldest first"}
        </button>
        <label className="text-xs text-zinc-500 flex items-center gap-1.5">
          Rows
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setSkip(0); }}
            className="text-xs bg-white border border-zinc-200 rounded px-1.5 py-0.5 hover:border-zinc-300 focus:outline-none focus:ring-1 focus:ring-zinc-300"
            data-testid="audit-page-size"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setSkip(Math.max(0, skip - pageSize))} disabled={skip === 0 || loading} className="p-1 rounded hover:bg-zinc-100 disabled:opacity-40" data-testid="audit-prev"><ChevronLeft className="h-4 w-4" /></button>
          <div className="text-xs text-zinc-600" data-testid="audit-page-indicator">Page {page} / {totalPages}</div>
          <button onClick={() => setSkip(Math.min((totalPages - 1) * pageSize, skip + pageSize))} disabled={page >= totalPages || loading} className="p-1 rounded hover:bg-zinc-100 disabled:opacity-40" data-testid="audit-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50/60 text-xs uppercase tracking-wider text-zinc-500">
          <tr>
            <th className="text-left px-5 py-3">When</th>
            <th className="text-left px-5 py-3">Action</th>
            <th className="text-left px-5 py-3">Target</th>
            <th className="text-left px-5 py-3">Changes</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan="4" className="px-5 py-10 text-center text-zinc-500">Loading…</td></tr>}
          {!loading && items.length === 0 && (
            <tr><td colSpan="4" className="px-5 py-10 text-center text-zinc-500" data-testid="empty-audit">No activity yet.</td></tr>
          )}
          {items.map((l) => (
            <tr key={l.id} className="border-t border-zinc-100" data-testid={`audit-row-${l.id}`}>
              <td className="px-5 py-3 text-zinc-500 text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
              <td className="px-5 py-3 font-medium">{l.action}</td>
              <td className="px-5 py-3 text-zinc-600">{l.target_type} <span className="text-zinc-400">·</span> <span className="font-mono text-xs">{l.target_id?.slice(0, 8)}</span></td>
              <td className="px-5 py-3 text-zinc-600 text-xs">{summarize(l.previous_value, l.new_value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function summarize(prev, next) {
  if (!next || typeof next !== "object") return "—";
  const keys = Object.keys(next).filter(k => !["created_at", "updated_at", "id", "password_hash"].includes(k));
  if (keys.length === 0) return "—";
  return keys.slice(0, 4).map((k) => {
    const before = prev?.[k];
    const after = next[k];
    if (before === undefined || JSON.stringify(before) === JSON.stringify(after)) {
      return `${k}: ${fmt(after)}`;
    }
    return `${k}: ${fmt(before)} → ${fmt(after)}`;
  }).join("  ·  ");
}

function fmt(v) {
  if (v == null) return "∅";
  if (typeof v === "object") return JSON.stringify(v).slice(0, 40);
  return String(v).slice(0, 60);
}
